// Клавиатура «как в Word» - одна схема для основного текста и малых редакторов (листы «Задание», «Реферат»,
// «Аннотация», раздел об ИИ, сноска):
//   Shift+Enter          - разрыв строки внутри абзаца (узел hardBreak; экспорт - w:br)
//   Ctrl+Shift+Пробел    - неразрывный пробел (U+00A0), Ctrl+Shift+- - неразрывный дефис (U+2011): только при
//                          USE_NBSP (core/schema/constants); пока он выключен - обычный пробел и дефис (в DOCX
//                          неразрывных знаков нет - Word показывал бы их «°»)
//   Ctrl+=  / Ctrl+Shift+= - нижний / верхний индекс (как в Word; Ctrl+, / Ctrl+. тоже работают)
//   Ctrl+Z или Backspace сразу после автоформата («- », «а) », «1) », «# », «$$ »…) - вернуть набранные знаки
//                          (одно действие; дальше Ctrl+Z - обычная отмена)
//   Tab / Shift+Tab      - в перечислении уровень, в таблице ячейки (их обрабатывают расширения списка и таблицы);
//                          в обычном абзаце Tab не уводит фокус из текста - абзацный отступ ставится сам (подсказка)
// Голые модификаторы (Ctrl+Shift, Alt+Shift - переключение раскладки Windows, Ctrl, Alt, Shift) не перехватываются.
// Раскладка не важна: сочетания сравниваются по физической клавише (event.code).
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection, type EditorState, type Transaction } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { closeHistory } from '@tiptap/pm/history';
import { USE_NBSP } from '../schema/constants';

export const NBSP = USE_NBSP ? String.fromCharCode(0xa0) : ' ';
export const NBHY = USE_NBSP ? String.fromCharCode(0x2011) : '-';
const MODIFIER_KEYS = new Set(['Control', 'Shift', 'Alt', 'Meta', 'AltGraph', 'CapsLock', 'OS', 'Hyper', 'Super', 'Fn']);

interface AutoUndo { transform: Transaction; from: number; to: number; text: string }
const autoKey = new PluginKey<AutoUndo | null>('stoAutoUndo');

/** Отмена, записанная правилами автоформата TipTap (у каждого набора правил - свой модуль с isInputRules). */
function inputRuleMeta(state: EditorState, tr: Transaction): AutoUndo | null {
  for (const p of state.plugins) {
    if (!(p.spec as any).isInputRules) continue;
    const m = tr.getMeta(p);
    if (m && m.transform) return { transform: m.transform, from: m.from, to: m.to, text: m.text };
  }
  return null;
}

/** Вернуть набранные знаки вместо автоформата (как Ctrl+Z после автозамены в Word). */
function undoAutoformat(view: EditorView): boolean {
  const st = autoKey.getState(view.state);
  if (!st) return false;
  const tr = view.state.tr;
  try {
    const t = st.transform;
    for (let j = t.steps.length - 1; j >= 0; j -= 1) tr.step(t.steps[j].invert(t.docs[j]));
    const marks = tr.doc.resolve(st.from).marks();
    if (st.text) tr.replaceWith(st.from, st.to, view.state.schema.text(st.text, marks));
    tr.setSelection(TextSelection.create(tr.doc, st.from + st.text.length));
  } catch {
    return false;
  }
  tr.setMeta(autoKey, 'clear');
  tr.setMeta('stoAutoUndo', true);
  view.dispatch(tr.scrollIntoView());
  return true;
}

function insertChar(view: EditorView, ch: string): boolean {
  const { state } = view;
  const { $from } = state.selection;
  if (!$from.parent.inlineContent) return false;
  view.dispatch(state.tr.insertText(ch).scrollIntoView());
  return true;
}

function toggleMark(view: EditorView, name: string, other: string): boolean {
  const { state } = view;
  const type = state.schema.marks[name];
  if (!type) return false;
  const { from, to, empty, $from } = state.selection;
  if (!$from.parent.inlineContent) return false;
  let tr = state.tr;
  const otherType = state.schema.marks[other];
  if (empty) {
    const marks = state.storedMarks || $from.marks();
    const has = !!type.isInSet(marks);
    let next = has ? type.removeFromSet(marks) : type.create().addToSet(marks);
    if (!has && otherType) next = otherType.removeFromSet(next);   // индекс - либо верхний, либо нижний
    tr = tr.setStoredMarks(next);
  } else {
    const has = state.doc.rangeHasMark(from, to, type);
    if (has) tr = tr.removeMark(from, to, type);
    else {
      if (otherType) tr = tr.removeMark(from, to, otherType);
      tr = tr.addMark(from, to, type.create());
    }
  }
  view.dispatch(tr);
  return true;
}

/** Заголовок 1 уровня с названием структурного элемента -> вид элемента (writer по тексту заголовка выгружает его
 *  элементом: без номера, по центру; «Список использованных источников» - только через СТО → Элемент). */
const STRUCT_ON_ENTER: Record<string, string> = {
  'введение': 'intro', 'выводы': 'conclusions', 'вывод': 'conclusions', 'заключение': 'conclusion',
  'нормативные ссылки': 'normrefs', 'определения, обозначения и сокращения': 'defs',
};
const STRUCT_GROUP: Record<string, string[]> = {
  intro: ['intro'], conclusions: ['conclusions', 'conclusion'], conclusion: ['conclusions', 'conclusion'],
  normrefs: ['normrefs'], defs: ['defs', 'terms', 'abbrev'],
};

/** Enter в конце заголовка раздела «Введение» / «Выводы» / «Заключение»… - заголовок становится структурным элементом
 *  (как его выгрузит writer: без номера, по центру), курсор - в новый абзац под ним. Иначе редактор показывал
 *  «1 Введение», а в DOCX/PDF - «Введение», и номера всех следующих разделов расходились. Ctrl+Z - вернуть заголовок. */
function structOnEnter(view: EditorView): boolean {
  const { state } = view;
  const sel = state.selection;
  const { $from } = sel;
  const h = $from.parent;
  const S = state.schema.nodes;
  if (!sel.empty || h.type.name !== 'heading' || Number(h.attrs.level) !== 1 || $from.depth !== 1) return false;
  if ($from.parentOffset !== h.content.size || !S.structHeading || !S.paragraph) return false;
  const key = h.textContent.trim().toLowerCase().replace(/ё/g, 'е').replace(/[\s ]+/g, ' ').replace(/\.$/, '');
  const kind = STRUCT_ON_ENTER[key];
  if (!kind) return false;
  let exists = false;
  state.doc.forEach((n) => { if (n.type.name === 'structHeading' && STRUCT_GROUP[kind].includes(n.attrs.kind)) exists = true; });
  if (exists) return false;          // второй такой элемент - обычный Enter (проверка СТО подскажет)
  const from = $from.before(1);
  const to = $from.after(1);
  const tr = closeHistory(state.tr);
  tr.replaceWith(from, to, [S.structHeading.create({ kind }), S.paragraph.create()]);
  const at = from + tr.doc.child(tr.doc.resolve(from).index(0)).nodeSize + 1;
  tr.setSelection(TextSelection.create(tr.doc, Math.min(at, tr.doc.content.size)));
  view.dispatch(tr.scrollIntoView());
  return true;
}

/** Место курсора: в перечислении / в таблице / в подписи / в обычном тексте. */
export function cursorContext(state: EditorState): { list: boolean; table: boolean; caption: boolean } {
  const { $from } = state.selection;
  let list = false; let table = false; let caption = false;
  for (let d = $from.depth; d > 0; d--) {
    const n = $from.node(d).type.name;
    if (n === 'stoListItem') list = true;
    if (n === 'tableCell' || n === 'tableHeader') table = true;
    if (n === 'caption') caption = true;
  }
  return { list, table, caption };
}

let lastHint = 0;
function hint(text: string) {
  const now = Date.now();
  if (now - lastHint < 8000) return;
  lastHint = now;
  window.dispatchEvent(new CustomEvent('sto-toast', { detail: { text, kind: 'info' } }));
}

/** Основная схема: высокий приоритет - раньше сочетаний команд (Ctrl+= - индекс, а не масштаб, пока курсор в тексте). */
export const WordKeysExtension = Extension.create({
  name: 'stoWordKeys',
  priority: 1200,
  addKeyboardShortcuts() {
    return {
      'Shift-Enter': () => {
        const ed = this.editor;
        if (!ed.schema.nodes.hardBreak) return false;
        const { $from } = ed.state.selection;
        if (!$from.parent.inlineContent || $from.parent.type.spec.code) return false;
        return ed.commands.setHardBreak();
      },
      Enter: () => structOnEnter(this.editor.view),
    };
  },
  addProseMirrorPlugins() {
    return [new Plugin<AutoUndo | null>({
      key: autoKey,
      state: {
        init: () => null,
        apply: (tr, prev, _old, state) => {
          const m = tr.getMeta(autoKey);
          if (m === 'clear') return null;
          const stored = inputRuleMeta(state, tr);
          if (stored) return stored;
          // служебные дописывания той же правки (id узлов, нумерация) - автоформат ещё можно отменить
          if (tr.getMeta('appendedTransaction')) return prev;
          return tr.docChanged || tr.selectionSet ? null : prev;
        },
      },
      props: {
        handleKeyDown: (view, e) => {
          if (MODIFIER_KEYS.has(e.key) || e.isComposing) return false;
          const ctrl = e.ctrlKey || e.metaKey;
          if (ctrl && !e.altKey) {
            if (e.shiftKey && e.code === 'Space') return insertChar(view, NBSP);
            if (e.shiftKey && e.code === 'Minus') return insertChar(view, NBHY);
            if (e.code === 'Equal') return toggleMark(view, e.shiftKey ? 'superscript' : 'subscript', e.shiftKey ? 'subscript' : 'superscript');
            if (!e.shiftKey && e.code === 'KeyZ') return undoAutoformat(view);
          }
          if (!ctrl && !e.altKey && !e.shiftKey && e.key === 'Backspace') return undoAutoformat(view);
          // новый абзац - новый шаг отмены (как в Word: Ctrl+Z убирает последний абзац, а не весь набранный текст)
          if (!ctrl && !e.altKey && e.key === 'Enter') view.dispatch(closeHistory(view.state.tr).setMeta('addToHistory', false));
          return false;
        },
      },
    })];
  },
});

/** Запасной Tab: то, что не обработали перечисление, таблица, подпись, код, - не уводит фокус из текста. */
export const TabFallbackExtension = Extension.create({
  name: 'stoTabFallback',
  priority: 1,
  addKeyboardShortcuts() {
    const tab = (shift: boolean) => () => {
      const c = cursorContext(this.editor.state);
      if (c.table) return false;
      if (c.list) {
        if (!shift) hint('Первую позицию перечисления нельзя сдвинуть ниже - уровень меняется у второй и следующих позиций (Tab), Shift+Tab - выше');
        return true;
      }
      hint('Абзацный отступ 1,25 см ставится сам - Tab не нужен. Tab / Shift+Tab меняют уровень перечисления, в таблице - переход по ячейкам');
      return true;
    };
    return { Tab: tab(false), 'Shift-Tab': tab(true) };
  },
});

export const wordKeys = () => [WordKeysExtension, TabFallbackExtension];
